import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Coins } from "lucide-react";
import { authAxios } from "../../utils/auth";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import AdminMenu from "../../components/AdminMenu";
import MainMenu from "../../components/MainMenu";

const dollars = (c) => `$${((c || 0) / 100).toFixed(2)}`;

export default function AuditBonusPool() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await authAxios.get("/admin/audit/bonus-pool");
      setData(res.data);
    } catch (e) {
      console.error("audit bonus load failed:", e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-divine-black text-white">
      <header className="border-b border-divine-gold/20 bg-divine-black-light/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/plog/audit")}
              className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5"
              data-testid="audit-bonus-back-btn"
              title="Back to audit"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-white font-medium tracking-widest flex items-center gap-2">
                <Coins className="w-5 h-5 text-divine-gold" />
                BONUS POOL AUDIT
              </h1>
              <p className="text-white/40 text-xs">Admin-only reconciliation</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={load}
              className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/10"
              title="Refresh"
              data-testid="audit-bonus-refresh-btn"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            </button>
            <AdminMenu />
            <MainMenu />
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {loading || !data ? (
          <p className="text-white/50">Loading…</p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MiniCard label="Inflows" value={dollars(data.inflows_cents)} tone="green" />
              <MiniCard label="Outflows" value={dollars(data.outflows_cents)} tone="red" />
              <MiniCard label="Net balance" value={dollars(data.net_balance_cents)} tone="neutral" strong />
              <MiniCard label="Live pool" value={dollars(data.live_balance_cents)} tone="neutral" />
            </div>

            <Card className="bg-white/5 border-white/10" data-testid="audit-bonus-groups">
              <CardHeader className="py-3">
                <CardTitle className="text-white text-sm">Movements by type</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-white/40 text-xs">
                      <th className="text-left font-normal py-1">Type</th>
                      <th className="text-right font-normal py-1">Amount</th>
                      <th className="text-right font-normal py-1">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.groups.map((g, i) => (
                      <tr key={i} className="border-t border-white/5">
                        <td className="py-1.5 text-white/85">{g.type}</td>
                        <td className="py-1.5 text-right font-mono">{dollars(g.sum_cents)}</td>
                        <td className="py-1.5 text-right text-white/50">{g.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10" data-testid="audit-bonus-tieouts">
              <CardHeader className="py-3">
                <CardTitle className="text-white text-sm">Invariants</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <ul className="space-y-2">
                  {data.tieouts.map((t) => (
                    <li
                      key={t.id}
                      className="flex items-start justify-between gap-3 py-2 border-b border-white/5 last:border-0"
                      data-testid={`audit-bonus-tieout-${t.id}`}
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <span
                          className={`mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                            t.classification === "green" ? "bg-green-400"
                              : t.classification === "amber" ? "bg-amber-400"
                              : "bg-red-400"
                          }`}
                        />
                        <div className="min-w-0">
                          <div className="text-white/85 text-sm">
                            <span className="font-semibold text-white/60">{t.id}</span> · {t.label}
                          </div>
                          {t.note && <div className="text-white/40 text-xs mt-0.5">{t.note}</div>}
                        </div>
                      </div>
                      <div className="text-right text-xs font-mono text-white/70 flex-shrink-0">
                        {t.count_style ? (
                          <>exp {t.expected_cents} / act {t.actual_cents}</>
                        ) : (
                          <>exp {dollars(t.expected_cents)} · act {dollars(t.actual_cents)}</>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

function MiniCard({ label, value, tone, strong }) {
  const dot = tone === "green" ? "bg-green-400" : tone === "red" ? "bg-red-400" : "bg-divine-gold";
  return (
    <div className="bg-white/[0.04] border border-white/10 rounded-xl p-4">
      <div className="flex items-center gap-2 text-white/50 text-xs mb-1">
        <span className={`w-2 h-2 rounded-full ${dot}`} />
        {label}
      </div>
      <div className={`text-white ${strong ? "text-xl font-semibold" : "text-lg"} tracking-tight`}>{value}</div>
    </div>
  );
}
